// ============================================================================
// MODULAR DESIGN PRINCIPLES (MANDATORY)
// ============================================================================
// This file strictly follows:
// 1. Single Responsibility Principle - Each component does ONE thing
// 2. Separation of Concerns - Logic separated from presentation
// 3. Modular Architecture - Independent, reusable components
//
// Refer to: docs/architecture/modular-design-standards.md
// ============================================================================

// Refer to docs/architecture/3-worker-architecture.md
/**
 * Real-time Task Log Viewer
 * 
 * Shows real-time logs and status updates for voice processing tasks
 * Helps debug stuck or failed jobs
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  Chip,
  IconButton,
  LinearProgress,
  Alert,
  AlertTitle,
  Divider,
  Stack,
  CircularProgress,
} from '@mui/material';
import {
  Close,
  Refresh,
  PlayArrow,
  Pause,
  Stop,
  Error as ErrorIcon,
  CheckCircle,
  HourglassEmpty,
  Psychology,
  Delete,
  Warning as WarningIcon,
  Replay,
  ContentCopy,
  ExpandMore,
} from '@mui/icons-material';
import { apiClient, API_ENDPOINTS } from '../config/api';
import { useAuth } from '../contexts/AuthContext';

const TaskLogViewer = ({ open, onClose, taskId, fileName, onTaskDeleted }) => {
  const { hasPermission } = useAuth();
  const [taskData, setTaskData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [logs, setLogs] = useState([]);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [retrying, setRetrying] = useState(false);
  const [retryError, setRetryError] = useState(null);
  const [rerunning, setRerunning] = useState(false);
  const [rerunError, setRerunError] = useState(null);
  const [processLogsExpanded, setProcessLogsExpanded] = useState(false);
  const [processLogCopyMessage, setProcessLogCopyMessage] = useState('');
  const logsEndRef = useRef(null);

  // Fetch task details and logs
  const fetchTaskData = async () => {
    if (!taskId) return;
    
    setLoading(true);
    try {
      // Get task details
      const response = await apiClient.get(`${API_ENDPOINTS.VOICE_TASKS}/${taskId}`);
      const responseData = response.data;
      
      // Extract task data from nested structure
      const data = responseData.task || responseData;
      
      setTaskData(data);
      
      // Build logs from task data
      const newLogs = [];
      
      // Timeline logs
      if (data.created_at) {
        newLogs.push({
          timestamp: data.created_at,
          level: 'info',
          message: `Task created: ${data.file_name}`,
          stage: 'created'
        });
      }
      
      if (data.started_at) {
        newLogs.push({
          timestamp: data.started_at,
          level: 'info',
          message: `Processing started`,
          stage: 'started'
        });
      }
      
      // Add detailed progress logs from progress_logs array
      if (data.progress_logs && Array.isArray(data.progress_logs)) {
        data.progress_logs.forEach((progressLog) => {
          newLogs.push({
            timestamp: progressLog.timestamp,
            level: progressLog.level || 'info',
            message: progressLog.message,
            stage: 'progress',
            code: progressLog.code,
            details: progressLog.details,
            segment_count: progressLog.segment_count || progressLog.details?.segment_count,
            elapsed_time: progressLog.elapsed_time || progressLog.details?.elapsed_time
          });
        });
      }
      
      // Current status (use server time when available — not "now" on every refresh)
      const statusTs =
        (data.status === 'failed' || data.status === 'cancelled') && data.completed_at
          ? data.completed_at
          : data.updated_at || new Date().toISOString();
      newLogs.push({
        timestamp: statusTs,
        level: data.status === 'failed' ? 'error' : 'info',
        message: `Current status: ${data.status}`,
        stage: data.status
      });
      
      // Transcription logs (GET /voice/tasks/{id} includes summary; no full text payload)
      if (data.transcription) {
        const charCount =
          data.transcription.character_count ??
          (typeof data.transcription.text === 'string'
            ? data.transcription.text.length
            : 0);
        const c = data.transcription.confidence;
        const confPct =
          c != null && !Number.isNaN(Number(c))
            ? `${(Number(c) * 100).toFixed(1)}`
            : 'N/A';
        newLogs.push({
          timestamp: data.transcription.created_at || new Date().toISOString(),
          level: 'success',
          message: `Transcription completed: ${charCount} characters, ${confPct}% confidence`,
          stage: 'transcription'
        });
      }
      
      // Analysis logs
      if (data.analysis) {
        newLogs.push({
          timestamp: data.analysis.created_at || new Date().toISOString(),
          level: 'success',
          message: `Analysis completed: QA Score ${data.analysis.qa_score || 'N/A'}/100`,
          stage: 'analysis'
        });
      }
      
      // Error log
      if (data.error_message) {
        newLogs.push({
          timestamp: new Date().toISOString(),
          level: 'error',
          message: `Error: ${data.error_message}`,
          stage: 'error'
        });
      }
      
      // Backend sets completed_at on both success (ready_to_analyse) and pipeline failure
      // (e.g. English translation handoff) — only show success wording when not failed/cancelled.
      if (data.completed_at && data.status !== 'failed' && data.status !== 'cancelled') {
        newLogs.push({
          timestamp: data.completed_at,
          level: 'success',
          message: `Task completed successfully`,
          stage: 'completed'
        });
      } else if (data.completed_at && data.status === 'failed') {
        newLogs.push({
          timestamp: data.completed_at,
          level: 'warning',
          message:
            'Processing run finished with failure (transcription may be saved; see error below).',
          stage: 'pipeline_failed'
        });
      }
      
      // Sort logs by timestamp
      newLogs.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
      
      setLogs(newLogs);
      
    } catch (error) {
      console.error('Failed to fetch task data:', error);
      setLogs([{
        timestamp: new Date().toISOString(),
        level: 'error',
        message: `Failed to fetch task data: ${error.message}`,
        stage: 'error'
      }]);
    } finally {
      setLoading(false);
    }
  };

  // Handle task retry
  const handleRetryTask = async () => {
    if (!taskId) return;
    
    try {
      setRetrying(true);
      setRetryError(null);
      
      const response = await apiClient.post(`${API_ENDPOINTS.VOICE_RETRY}/${taskId}/retry`);
      
      if (response.data.success) {
        // Refresh task data to show updated status
        await fetchTaskData();
        
        // Show success message
        setLogs(prevLogs => [...prevLogs, {
          timestamp: new Date().toISOString(),
          level: 'success',
          message: `Task retry initiated successfully`,
          stage: 'retry'
        }]);
      } else {
        throw new Error(response.data.error || 'Failed to retry task');
      }
    } catch (error) {
      console.error('Error retrying task:', error);
      setRetryError(error.response?.data?.detail || error.message || 'Failed to retry task');
      
      // Add error log with better formatting
      const errorMessage = error.response?.data?.detail || error.message;
      setLogs(prevLogs => [...prevLogs, {
        timestamp: new Date().toISOString(),
        level: 'error',
        message: `Retry failed: ${errorMessage}`,
        stage: 'retry_error'
      }]);
    } finally {
      setRetrying(false);
    }
  };

  // Handle task rerun (for completed tasks)
  const handleRerunTask = async (qaCriteriaId = null) => {
    if (!taskId) return;
    
    try {
      setRerunning(true);
      setRerunError(null);
      
      const payload = qaCriteriaId ? { qa_criteria_id: qaCriteriaId } : {};
      const response = await apiClient.post(`${API_ENDPOINTS.VOICE_TASK_RERUN}/${taskId}/rerun`, payload);
      
      if (response.data.success) {
        // Refresh task data
        await fetchTaskData();
        
        // Add success log
        setLogs(prevLogs => [...prevLogs, {
          timestamp: new Date().toISOString(),
          level: 'success',
          message: `Task rerun initiated successfully`,
          stage: 'rerun'
        }]);
      } else {
        throw new Error(response.data.error || 'Failed to rerun task');
      }
    } catch (error) {
      console.error('Error rerunning task:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error response status:', error.response?.status);
      
      // Extract error message from response (support multiple formats)
      let errorMessage = 'Failed to rerun task';
      
      if (error.response?.data) {
        // Try different error message fields
        errorMessage = 
          error.response.data.message ||  // Custom error format: {error: true, message: "..."}
          error.response.data.detail ||   // FastAPI default: {detail: "..."}
          (typeof error.response.data.error === 'string' ? error.response.data.error : null) ||  // String error field
          errorMessage;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      // Check if error is related to file not found
      const errorLower = errorMessage.toLowerCase();
      if (errorLower.includes('file') && (errorLower.includes('missing') || errorLower.includes('not found') || errorLower.includes('deleted'))) {
        errorMessage = 'File not found: The original voice file has been deleted. Rerun is only available for tasks where the voice file still exists.';
      }
      
      console.log('Setting rerun error:', errorMessage);
      setRerunError(errorMessage);
      
      // Add error log
      setLogs(prevLogs => [...prevLogs, {
        timestamp: new Date().toISOString(),
        level: 'error',
        message: `Rerun failed: ${errorMessage}`,
        stage: 'rerun_error'
      }]);
    } finally {
      setRerunning(false);
    }
  };

  // Handle task deletion
  const handleDeleteTask = () => {
    setDeleteConfirmOpen(true);
    setDeleteError(null);
  };

  const confirmDeleteTask = async () => {
    if (!taskId) return;
    
    try {
      setDeleting(true);
      setDeleteError(null);
      
      const response = await apiClient.delete(`${API_ENDPOINTS.VOICE_DELETE}/${taskId}`);
      
      if (response.data.success) {
        // Close both modals
        setDeleteConfirmOpen(false);
        onClose();
        
        // Notify parent component that task was deleted
        if (onTaskDeleted) {
          onTaskDeleted(taskId);
        }
      } else {
        throw new Error(response.data.message || 'Failed to delete task');
      }
    } catch (error) {
      console.error('Error deleting task:', error);
      setDeleteError(error.response?.data?.detail || error.message || 'Failed to delete task');
    } finally {
      setDeleting(false);
    }
  };

  // Auto-refresh when modal is open
  // Clear error states when taskId changes or modal opens
  useEffect(() => {
    if (open && taskId) {
      // Clear all error states when opening a new task
      setRerunError(null);
      setRetryError(null);
      setDeleteError(null);
    }
  }, [taskId, open]);

  useEffect(() => {
    if (open && taskId) {
      fetchTaskData();

      if (autoRefresh) {
        const interval = setInterval(fetchTaskData, 3000); // Refresh every 3 seconds
        return () => clearInterval(interval);
      }
    }
  }, [open, taskId, autoRefresh]);

  // Scroll to bottom when logs update
  useEffect(() => {
    logsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [logs]);

  const getStatusColor = (status) => {
    const colors = {
      'queued': 'default',
      'processing': 'primary',
      'ready_to_analyse': 'info',
      'analysing': 'secondary',
      'completed': 'success',
      'failed': 'error',
      'cancelled': 'warning'
    };
    return colors[status] || 'default';
  };

  const getStatusIcon = (status) => {
    const icons = {
      'queued': <HourglassEmpty />,
      'processing': <PlayArrow />,
      'ready_to_analyse': <Psychology />,
      'analysing': <Psychology />,
      'completed': <CheckCircle />,
      'failed': <ErrorIcon />,
    };
    return icons[status] || <HourglassEmpty />;
  };

  const getLevelColor = (level) => {
    const colors = {
      'info': 'info.main',
      'success': 'success.main',
      'warning': 'warning.main',
      'error': 'error.main',
    };
    return colors[level] || 'text.primary';
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-GB', { 
      hour: '2-digit', 
      minute: '2-digit', 
      second: '2-digit'
    });
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString('en-GB', {
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const stringifyDetailValue = (value) => {
    if (value === null || value === undefined || value === '') return null;
    if (Array.isArray(value)) {
      return value
        .map((item) =>
          item !== null && typeof item === 'object'
            ? JSON.stringify(item)
            : String(item)
        )
        .join(', ');
    }
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  const processLogs = useMemo(() => {
    if (!taskData?.progress_logs || !Array.isArray(taskData.progress_logs)) return [];
    return taskData.progress_logs
      .filter((entry) => {
        const code = String(entry?.code || '');
        return !code.startsWith('analysis_');
      })
      .map((entry, index) => ({ ...entry, index }))
      .sort((a, b) => new Date(a.timestamp || 0) - new Date(b.timestamp || 0));
  }, [taskData]);

  const processLogText = useMemo(() => {
    if (!taskData) return '';
    const lines = [
      '# Voice Process Logs',
      '',
      '## Task',
      `- task_id: ${taskData.id || taskId || 'N/A'}`,
      `- file_name: ${taskData.file_name || fileName || 'N/A'}`,
      `- status: ${taskData.status || 'N/A'}`,
      `- language: ${taskData.original_language || 'N/A'}`,
      `- created_at: ${taskData.created_at || 'N/A'}`,
      `- started_at: ${taskData.started_at || 'N/A'}`,
      `- completed_at: ${taskData.completed_at || 'N/A'}`,
      '',
      '## Process Steps',
    ];

    if (processLogs.length === 0) {
      lines.push('- No detailed process logs available.');
      return lines.join('\n');
    }

    processLogs.forEach((entry, index) => {
      lines.push(
        `- ${index + 1}. [${entry.timestamp || 'N/A'}] ${entry.level || 'info'} ${entry.code ? `(${entry.code}) ` : ''}: ${entry.message || ''}`
      );
      if (entry.details && typeof entry.details === 'object') {
        Object.entries(entry.details).forEach(([key, value]) => {
          const rendered = stringifyDetailValue(value);
          if (rendered !== null) {
            lines.push(`  - ${key}: ${rendered}`);
          }
        });
      }
    });
    return lines.join('\n');
  }, [fileName, processLogs, taskData, taskId]);

  const copyProcessLogs = async () => {
    try {
      await navigator.clipboard.writeText(processLogText || '');
      setProcessLogCopyMessage('Voice process logs copied.');
    } catch {
      setProcessLogCopyMessage('Copy failed. Check browser clipboard permission.');
    }
  };

  const calculateDuration = () => {
    if (!taskData) return null;
    
    const start = taskData.started_at ? new Date(taskData.started_at) : null;
    const end = taskData.completed_at ? new Date(taskData.completed_at) : new Date();
    
    if (!start) return null;
    
    const durationMs = end - start;
    const durationSec = Math.floor(durationMs / 1000);
    const minutes = Math.floor(durationSec / 60);
    const seconds = durationSec % 60;
    
    return `${minutes}m ${seconds}s`;
  };

  // Check if task is stuck
  const isTaskStuck = () => {
    if (!taskData) return false;
    
    // Only check for active statuses
    const activeStatuses = ['processing', 'analysing', 'ready_to_analyse', 'queued'];
    if (!activeStatuses.includes(taskData.status)) return false;
    
    const startedAt = taskData.started_at ? new Date(taskData.started_at) : null;
    if (!startedAt) return false;
    
    const now = new Date();
    const elapsedMinutes = (now - startedAt) / 1000 / 60;
    
    // Thresholds for stuck detection
    const thresholds = {
      'queued': 5,           // 5 minutes in queue is suspicious
      'processing': 30,      // 30 minutes processing is too long
      'ready_to_analyse': 10, // 10 minutes waiting for analysis
      'analysing': 15        // 15 minutes analyzing is too long
    };
    
    const threshold = thresholds[taskData.status] || 30;
    return elapsedMinutes > threshold;
  };

  // Get time since last update
  const getTimeSinceLastUpdate = () => {
    if (!taskData) return null;
    
    // Check for most recent timestamp
    const timestamps = [
      taskData.completed_at,
      taskData.started_at,
      taskData.created_at
    ].filter(t => t).map(t => new Date(t));
    
    if (timestamps.length === 0) return null;
    
    const latest = new Date(Math.max(...timestamps));
    const now = new Date();
    const diffMinutes = Math.floor((now - latest) / 1000 / 60);
    
    if (diffMinutes < 1) return 'Just now';
    if (diffMinutes === 1) return '1 minute ago';
    if (diffMinutes < 60) return `${diffMinutes} minutes ago`;
    
    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours === 1) return '1 hour ago';
    return `${diffHours} hours ago`;
  };

  if (!open) return null;

  return (
    <>
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '70vh', maxHeight: '90vh' }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6">
              Task Logs - {fileName}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
              Task ID: {taskId}
            </Typography>
          </Box>
          <Box display="flex" gap={1} alignItems="center">
            <IconButton 
              size="small" 
              onClick={() => setAutoRefresh(!autoRefresh)}
              color={autoRefresh ? 'primary' : 'default'}
            >
              {autoRefresh ? <Pause /> : <PlayArrow />}
            </IconButton>
            <IconButton size="small" onClick={fetchTaskData}>
              <Refresh />
            </IconButton>
            <IconButton onClick={onClose} size="small">
              <Close />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading && !taskData ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : taskData ? (
          <Box>
            {/* Task Overview */}
            <Paper elevation={2} sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
              <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                <Box display="flex" alignItems="center" gap={1}>
                  {getStatusIcon(taskData.status)}
                  <Chip 
                    label={taskData.status?.toUpperCase()} 
                    color={getStatusColor(taskData.status)}
                    size="small"
                  />
                </Box>
                
                {taskData.started_at && (
                  <Typography variant="body2" color="text.secondary">
                    Duration: {calculateDuration()}
                  </Typography>
                )}
                
                {/* Last Activity Indicator */}
                <Chip 
                  label={`Last activity: ${getTimeSinceLastUpdate()}`}
                  size="small"
                  variant="outlined"
                  color={isTaskStuck() ? 'error' : 'default'}
                />
                
                {taskData.retry_count > 0 && (
                  <Chip 
                    label={`Retries: ${taskData.retry_count}`}
                    size="small"
                    color="warning"
                  />
                )}
                
                {autoRefresh && (
                  <Chip 
                    label="Auto-refreshing (3s)"
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                )}
              </Stack>
              
              {/* Progress Indicator for active tasks */}
              {['processing', 'analysing', 'ready_to_analyse'].includes(taskData.status) && !isTaskStuck() && (
                <Box sx={{ mt: 2 }}>
                  <LinearProgress />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    Task in progress... Logs updating every 3 seconds
                  </Typography>
                </Box>
              )}
            </Paper>

            {/* STUCK TASK WARNING */}
            {isTaskStuck() && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" fontWeight="bold">
                  🚨 TASK APPEARS TO BE STUCK!
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  This task has been in "{taskData.status}" status for {calculateDuration()}.
                  Last activity was {getTimeSinceLastUpdate()}.
                </Typography>
                <Box sx={{ mt: 1.5, pl: 2 }}>
                  <Typography variant="caption" component="div">
                    <strong>Possible causes:</strong>
                  </Typography>
                  <Typography variant="caption" component="div">
                    • Worker crashed or stopped
                  </Typography>
                  <Typography variant="caption" component="div">
                    • File is too large or corrupted
                  </Typography>
                  <Typography variant="caption" component="div">
                    • System resources exhausted
                  </Typography>
                  
                  <Typography variant="caption" component="div" sx={{ mt: 1 }}>
                    <strong>Recommended actions:</strong>
                  </Typography>
                  <Typography variant="caption" component="div">
                    1. Check if workers are running: <code>python3 scripts/process-worker.py status</code>
                  </Typography>
                  <Typography variant="caption" component="div">
                    2. Check worker logs: <code>tail -f logs/workers/process-worker.log</code>
                  </Typography>
                  <Typography variant="caption" component="div">
                    3. Cancel this task and restart workers if needed
                  </Typography>
                </Box>
              </Alert>
            )}

            {/* Error Alert */}
            {taskData.error_message && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <Typography variant="subtitle2">Error Details:</Typography>
                <Typography variant="body2">{taskData.error_message}</Typography>
              </Alert>
            )}

            {/* Retry Error Alert */}
            {retryError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <Typography variant="subtitle2">Retry Failed:</Typography>
                <Typography variant="body2">{retryError}</Typography>
                {retryError.includes('files have been deleted') && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      💡 <strong>Solution:</strong> The original voice files have been cleaned up. 
                      You'll need to re-upload the files to retry processing.
                    </Typography>
                  </Box>
                )}
              </Alert>
            )}

            {/* Rerun Error Alert */}
            {rerunError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setRerunError(null)}>
                <AlertTitle>Rerun Failed</AlertTitle>
                <Typography variant="body2">{rerunError}</Typography>
                {(rerunError.toLowerCase().includes('file') && (rerunError.toLowerCase().includes('not found') || rerunError.toLowerCase().includes('missing') || rerunError.toLowerCase().includes('deleted'))) && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      💡 <strong>Solution:</strong> The original voice files have been cleaned up. 
                      Rerun is only available for tasks where the voice file still exists.
                    </Typography>
                  </Box>
                )}
              </Alert>
            )}

            {/* Logs Section */}
            <Paper 
              elevation={1} 
              sx={{ 
                p: 2, 
                bgcolor: '#1e1e1e', 
                color: '#fff',
                fontFamily: 'monospace',
                fontSize: '13px',
                maxHeight: '400px',
                overflowY: 'auto'
              }}
            >
              <Typography variant="subtitle2" gutterBottom sx={{ color: '#4fc3f7' }}>
                📋 Task Timeline & Logs
              </Typography>
              
              <Divider sx={{ mb: 2, bgcolor: '#424242' }} />
              
              {logs.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No logs available
                </Typography>
              ) : (
                logs.map((log, index) => (
                  <Box 
                    key={index} 
                    sx={{ 
                      mb: 1.5, 
                      pb: 1.5, 
                      borderBottom: index < logs.length - 1 ? '1px solid #333' : 'none' 
                    }}
                  >
                    <Box display="flex" gap={2} alignItems="flex-start">
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: '#757575',
                          minWidth: '75px',
                          fontFamily: 'monospace'
                        }}
                      >
                        {formatTimestamp(log.timestamp)}
                      </Typography>
                      
                      <Box 
                        sx={{ 
                          width: '8px', 
                          height: '8px', 
                          borderRadius: '50%', 
                          bgcolor: getLevelColor(log.level),
                          mt: 0.5
                        }} 
                      />
                      
                      <Box sx={{ flex: 1 }}>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: log.level === 'error' ? '#f44336' : 
                                   log.level === 'success' ? '#4caf50' :
                                   log.level === 'warning' ? '#ff9800' : '#fff'
                          }}
                        >
                          {log.message}
                        </Typography>
                        {log.stage === 'progress' && log.segment_count && (
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              color: '#757575',
                              display: 'block',
                              mt: 0.5,
                              fontFamily: 'monospace',
                              fontSize: '0.75rem'
                            }}
                          >
                            📊 Segments: {log.segment_count} | ⏱️ Elapsed: {log.elapsed_time?.toFixed(1)}s
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>
                ))
              )}
              
              <div ref={logsEndRef} />
            </Paper>

            {/* Task Details */}
            <Paper elevation={1} sx={{ p: 2, mt: 2, bgcolor: 'grey.50' }}>
              <Typography variant="subtitle2" gutterBottom>
                📊 Task Details
              </Typography>
              
              <Stack spacing={1} sx={{ mt: 1 }}>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">File Name:</Typography>
                  <Typography variant="body2">{taskData.file_name}</Typography>
                </Box>
                
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">File Size:</Typography>
                  <Typography variant="body2">
                    {taskData.file_size ? `${(taskData.file_size / 1024 / 1024).toFixed(2)} MB` : 'N/A'}
                  </Typography>
                </Box>
                
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">QA Criteria:</Typography>
                  <Typography variant="body2">{taskData.qa_criteria_id || 'None'}</Typography>
                </Box>
                
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">Priority:</Typography>
                  <Typography variant="body2">{taskData.priority || 0}</Typography>
                </Box>
                
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">Retry Count:</Typography>
                  <Typography variant="body2">{taskData.retry_count || 0}</Typography>
                </Box>
                
                {taskData.started_at && (
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">Duration:</Typography>
                    <Typography variant="body2">{calculateDuration()}</Typography>
                  </Box>
                )}
                
                {/* Processing Progress */}
                {taskData.transcription && (
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">Transcription:</Typography>
                    <Chip label="✅ Complete" color="success" size="small" />
                  </Box>
                )}
                
                {taskData.analysis && (
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">Analysis:</Typography>
                    <Chip label="✅ Complete" color="success" size="small" />
                  </Box>
                )}
              </Stack>
            </Paper>

            {/* Detailed Voice Process Logs */}
            <Accordion
              expanded={processLogsExpanded}
              onChange={(_, nextExpanded) => {
                setProcessLogsExpanded(nextExpanded);
                if (nextExpanded) setProcessLogCopyMessage('');
              }}
              sx={{ mt: 2 }}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box>
                  <Typography variant="subtitle2">
                    Detailed Voice Process Logs
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Expand to inspect every process pipeline step captured for this task.
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={1.5}>
                  {processLogCopyMessage && (
                    <Alert
                      severity={processLogCopyMessage.startsWith('Copy failed') ? 'error' : 'success'}
                      onClose={() => setProcessLogCopyMessage('')}
                    >
                      {processLogCopyMessage}
                    </Alert>
                  )}
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<Refresh />}
                      onClick={fetchTaskData}
                      disabled={loading}
                    >
                      Refresh
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<ContentCopy />}
                      onClick={copyProcessLogs}
                      disabled={!processLogText}
                    >
                      Copy Logs
                    </Button>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`${processLogs.length} event${processLogs.length === 1 ? '' : 's'}`}
                    />
                  </Stack>

                  <Typography
                    component="pre"
                    variant="body2"
                    sx={{
                      p: 2,
                      bgcolor: '#111827',
                      color: '#e5e7eb',
                      borderRadius: 1,
                      whiteSpace: 'pre-wrap',
                      maxHeight: 360,
                      overflow: 'auto',
                      fontFamily: 'monospace',
                      fontSize: '0.78rem',
                    }}
                  >
                    {processLogText || 'No detailed process logs available.'}
                  </Typography>

                  {processLogs.length > 0 && (
                    <Stack spacing={1}>
                      {processLogs.map((entry) => (
                        <Paper
                          key={`${entry.timestamp || 'no-ts'}-${entry.index}`}
                          variant="outlined"
                          sx={{
                            p: 1.25,
                            bgcolor:
                              entry.level === 'error'
                                ? 'error.lighter'
                                : entry.level === 'warning'
                                  ? '#fff8e1'
                                  : entry.level === 'success'
                                    ? '#e8f5e9'
                                    : 'grey.50',
                            borderLeft: '4px solid',
                            borderLeftColor:
                              entry.level === 'error'
                                ? 'error.main'
                                : entry.level === 'warning'
                                  ? 'warning.main'
                                  : entry.level === 'success'
                                    ? 'success.main'
                                    : 'grey.400',
                          }}
                        >
                          <Stack spacing={0.5}>
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              flexWrap="wrap"
                            >
                              <Chip
                                size="small"
                                color={
                                  entry.level === 'error'
                                    ? 'error'
                                    : entry.level === 'warning'
                                      ? 'warning'
                                      : entry.level === 'success'
                                        ? 'success'
                                        : 'info'
                                }
                                label={entry.level || 'info'}
                              />
                              {entry.code && (
                                <Typography
                                  variant="caption"
                                  sx={{ fontFamily: 'monospace' }}
                                >
                                  {entry.code}
                                </Typography>
                              )}
                              <Typography variant="caption" color="text.secondary">
                                {formatDateTime(entry.timestamp)}
                              </Typography>
                            </Stack>
                            <Typography variant="body2">{entry.message}</Typography>
                            {entry.details && Object.keys(entry.details).length > 0 && (
                              <Box
                                component="dl"
                                sx={{
                                  display: 'grid',
                                  gridTemplateColumns: '180px 1fr',
                                  gap: 0.5,
                                  m: 0,
                                }}
                              >
                                {Object.entries(entry.details).map(([key, value]) => {
                                  const rendered = stringifyDetailValue(value);
                                  if (rendered === null) return null;
                                  return (
                                    <React.Fragment key={key}>
                                      <Typography
                                        component="dt"
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        {key}
                                      </Typography>
                                      <Typography
                                        component="dd"
                                        variant="caption"
                                        sx={{ m: 0, fontFamily: 'monospace' }}
                                      >
                                        {rendered}
                                      </Typography>
                                    </React.Fragment>
                                  );
                                })}
                              </Box>
                            )}
                          </Stack>
                        </Paper>
                      ))}
                    </Stack>
                  )}
                </Stack>
              </AccordionDetails>
            </Accordion>

            {/* Normal Progress Info - Only show if NOT stuck */}
            {['processing', 'analysing', 'ready_to_analyse'].includes(taskData.status) && !isTaskStuck() && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="subtitle2">✅ Task is processing normally</Typography>
                <Typography variant="caption">
                  • Last activity: {getTimeSinceLastUpdate()}<br />
                  • Large files (5+ min) can take 10-15 minutes<br />
                  • Auto-refreshing every 3 seconds
                </Typography>
              </Alert>
            )}
          </Box>
        ) : logs.length > 0 ? (
          <Alert severity="error">
            <AlertTitle>Could not load task</AlertTitle>
            {logs.map((log, i) => (
              <Typography key={i} variant="body2" sx={{ mt: i ? 1 : 0 }}>
                {log.message}
              </Typography>
            ))}
          </Alert>
        ) : (
          <Alert severity="warning">No task data available</Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button
          startIcon={autoRefresh ? <Pause /> : <PlayArrow />}
          onClick={() => setAutoRefresh(!autoRefresh)}
          size="small"
        >
          {autoRefresh ? 'Pause' : 'Resume'} Auto-refresh
        </Button>
        
        {/* Retry Button - Only show for failed tasks */}
        {taskData?.status === 'failed' && hasPermission('VOICE_RETRY') && (
          <Button
            startIcon={retrying ? <CircularProgress size={16} /> : <Replay />}
            onClick={handleRetryTask}
            disabled={retrying}
            color="primary"
            variant="outlined"
            size="small"
          >
            {retrying ? 'Retrying...' : 'Retry Task'}
          </Button>
        )}
        
        {/* Rerun Button - Only show for completed tasks */}
        {taskData?.status === 'completed' && hasPermission('VOICE_RETRY') && (
          <Button
            startIcon={rerunning ? <CircularProgress size={16} /> : <Replay />}
            onClick={() => handleRerunTask()}
            disabled={rerunning}
            color="secondary"
            variant="outlined"
            size="small"
          >
            {rerunning ? 'Rerunning...' : 'Re-run Process'}
          </Button>
        )}
        
        {hasPermission('VOICE_DELETE') && (
          <Button
            startIcon={<Delete />}
            onClick={handleDeleteTask}
            disabled={deleting}
            color="error"
            size="small"
          >
            {deleting ? 'Deleting...' : 'Delete Task'}
          </Button>
        )}
        <Button onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>

    {/* Delete Confirmation Modal */}
    <Dialog
      open={deleteConfirmOpen}
      onClose={() => setDeleteConfirmOpen(false)}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
        <WarningIcon />
        Confirm Task Deletion
      </DialogTitle>
      
      <DialogContent>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Are you sure you want to delete this voice processing task?
        </Typography>
        
        <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1, mb: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 1 }}>
            Task Details:
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>File:</strong> {fileName || 'Unknown'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Task ID:</strong> {taskId}
          </Typography>
        </Box>
        
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>This action cannot be undone and will permanently remove:</strong>
          </Typography>
          <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
            <Typography component="li" variant="body2">Task record</Typography>
            <Typography component="li" variant="body2">Analysis results</Typography>
            <Typography component="li" variant="body2">Transcription data</Typography>
            <Typography component="li" variant="body2">All associated logs</Typography>
          </Box>
        </Alert>
        
        {deleteError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {deleteError}
          </Alert>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button 
          onClick={() => setDeleteConfirmOpen(false)}
          disabled={deleting}
        >
          Cancel
        </Button>
        <Button
          onClick={confirmDeleteTask}
          color="error"
          variant="contained"
          disabled={deleting}
          startIcon={deleting ? <CircularProgress size={16} /> : <Delete />}
        >
          {deleting ? 'Deleting...' : 'Delete Task'}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default TaskLogViewer;

